import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { 
  FinanceTaxSetting, 
  WithholdingTaxRate, 
  CreateWithholdingRateInput, 
  UpdateWithholdingRateInput 
} from '@/types/database/tax-settings';

// Fetch all tax settings
export function useTaxSettings() {
  return useQuery({
    queryKey: ['finance-tax-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('finance_tax_settings')
        .select('*');
      
      if (error) throw error;
      return data as FinanceTaxSetting[];
    },
  });
}

// Get default VAT rate
export function useVatRate() {
  return useQuery({
    queryKey: ['vat-rate'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('finance_tax_settings')
        .select('setting_value')
        .eq('setting_key', 'default_vat_rate')
        .maybeSingle();
      
      if (error) throw error;
      return data?.setting_value ?? 15;
    },
  });
}

// Update VAT rate
export function useUpdateVatRate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (newRate: number) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('finance_tax_settings')
        .update({ 
          setting_value: newRate,
          updated_at: new Date().toISOString(),
          updated_by: user?.id 
        })
        .eq('setting_key', 'default_vat_rate')
        .select()
        .maybeSingle();
      
      if (error) throw error;
      if (!data) throw new Error('VAT rate setting was not found');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vat-rate'] });
      queryClient.invalidateQueries({ queryKey: ['finance-tax-settings'] });
      toast.success('VAT rate updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update VAT rate: ' + error.message);
    },
  });
}

// Fetch all withholding rates
export function useWithholdingRates() {
  return useQuery({
    queryKey: ['withholding-rates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('withholding_tax_rates')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as WithholdingTaxRate[];
    },
  });
}

// Fetch only active withholding rates (for dropdowns)
export function useActiveWithholdingRates() {
  return useQuery({
    queryKey: ['withholding-rates', 'active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('withholding_tax_rates')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as WithholdingTaxRate[];
    },
  });
}

// Create withholding rate
export function useCreateWithholdingRate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: CreateWithholdingRateInput) => {
      // If setting as default, unset other defaults first
      if (input.is_default) {
        await supabase
          .from('withholding_tax_rates')
          .update({ is_default: false })
          .eq('is_default', true);
      }
      
      const { data, error } = await supabase
        .from('withholding_tax_rates')
        .insert(input)
        .select()
        .maybeSingle();
      
      if (error) throw error;
      if (!data) throw new Error('Withholding rate was not created');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withholding-rates'] });
      toast.success('Withholding rate created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create withholding rate: ' + error.message);
    },
  });
}

// Update withholding rate
export function useUpdateWithholdingRate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateWithholdingRateInput & { id: string }) => {
      // If setting as default, unset other defaults first
      if (input.is_default) {
        await supabase
          .from('withholding_tax_rates')
          .update({ is_default: false })
          .neq('id', id);
      }
      
      const { data, error } = await supabase
        .from('withholding_tax_rates')
        .update(input)
        .eq('id', id)
        .select()
        .maybeSingle();
      
      if (error) throw error;
      if (!data) throw new Error('Withholding rate was not found');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withholding-rates'] });
      toast.success('Withholding rate updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update withholding rate: ' + error.message);
    },
  });
}

// Delete withholding rate
export function useDeleteWithholdingRate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('withholding_tax_rates')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withholding-rates'] });
      toast.success('Withholding rate deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete withholding rate: ' + error.message);
    },
  });
}

// Get default withholding rate
export function useDefaultWithholdingRate() {
  return useQuery({
    queryKey: ['withholding-rates', 'default'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('withholding_tax_rates')
        .select('*')
        .eq('is_default', true)
        .eq('is_active', true)
        .maybeSingle();
      
      if (error) throw error;
      return data as WithholdingTaxRate | null;
    },
  });
}
